/**
 * Where the SDK keeps what it remembers in the browser, injectable.
 *
 * Two kinds, as the web platform has them: `local` outlives the tab (a
 * guest's display name), `session` dies with it (the resume credentials:
 * two tabs are two participants). Defaults are the page's own storage when
 * there is one; a test, a server render or a native shell passes its own,
 * or `null` to remember nothing.
 */

export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export interface StorageOptions {
  local?: KeyValueStore | null
  session?: KeyValueStore | null
}

export interface ResolvedStorage {
  local: KeyValueStore | null
  session: KeyValueStore | null
}

function pageStorage(kind: 'localStorage' | 'sessionStorage'): KeyValueStore | null {
  try {
    return ((globalThis as Record<string, unknown>)[kind] as KeyValueStore | null) ?? null
  } catch {
    // Some browsers throw on access when site data is blocked.
    return null
  }
}

export function resolveStorage(opts: StorageOptions = {}): ResolvedStorage {
  return {
    local: opts.local === undefined ? pageStorage('localStorage') : opts.local,
    session: opts.session === undefined ? pageStorage('sessionStorage') : opts.session,
  }
}

/** Read one key, never throwing: a blocked or full store is "nothing remembered". */
export function readKey(store: KeyValueStore | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null
  } catch {
    return null
  }
}

export function writeKey(store: KeyValueStore | null, key: string, value: string | null): void {
  try {
    if (value === null) store?.removeItem(key)
    else store?.setItem(key, value)
  } catch {
    // Not remembered; the next visit starts fresh.
  }
}

/** An in-memory store, for tests and for runtimes with no storage. */
export function memoryStorage(): KeyValueStore {
  const map = new Map<string, string>()
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => void map.set(k, String(v)),
    removeItem: (k) => void map.delete(k),
  }
}
